import React from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import clsx from 'clsx';
import PulsatingDots from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/PulsatingDots';
import isNil from 'lodash/isNil';

export type EOBScanStatus = 'idle' | 'processing' | 'completed' | 'failed';

type Props = {
  scanStatus: EOBScanStatus;
  errorMessage: string | null;
  onClick: () => void;
};

const STATUS_LABELS: Record<EOBScanStatus, string> = {
  processing: 'Scanning EOB…',
  completed: 'EOB ready — click to review',
  failed: 'Scan failed — click to retry',
  idle: 'Scan EOB',
};

export default function MinimizedEOBScan({
  scanStatus,
  errorMessage,
  onClick,
}: Props) {
  const isProcessing = scanStatus === 'processing';
  const isCompleted = scanStatus === 'completed';
  const isFailed = scanStatus === 'failed';

  return (
    <div className="absolute bottom-10 right-10 z-50">
      <div
        role="button"
        tabIndex={0}
        className={clsx(
          'flex items-center rounded-full shadow-lg cursor-pointer transition-all duration-300',
          'hover:bg-gray-600 pl-2 pr-4 py-2 overflow-hidden',
          isFailed ? 'bg-red-900 hover:bg-red-800' : 'bg-gray-700',
        )}
        style={{
          boxShadow: isCompleted
            ? '0 4px 14px 0 rgba(34, 197, 94, 0.4)'
            : '0 4px 14px 0 rgba(147, 197, 253, 0.4)',
        }}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
      >
        <div className="relative">
          {isProcessing && (
            <>
              <div
                className="absolute inset-0 rounded-full border-2 border-blue-300 opacity-80"
                style={{
                  animation: 'recordingRipple 2s linear infinite',
                  animationDelay: '0s',
                  transform: 'scale(1.2)',
                }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-blue-400 opacity-60"
                style={{
                  animation: 'recordingRipple 2s linear infinite',
                  animationDelay: '0.5s',
                  transform: 'scale(1.4)',
                }}
              />
            </>
          )}
          <div
            className={clsx(
              'w-10 h-10 rounded-full flex items-center justify-center relative z-10',
              isCompleted && 'bg-green-500',
              isFailed && 'bg-red-500',
              !isCompleted && !isFailed && 'bg-blue-500',
            )}
            style={{
              boxShadow: isProcessing
                ? '0 0 15px rgba(59, 130, 246, 0.8)'
                : '0 2px 8px rgba(59, 130, 246, 0.3)',
            }}
          >
            <ArticleRoundedIcon
              className={clsx(
                'text-white transition-all duration-300 !w-5 !h-5',
                isProcessing && 'animate-pulse',
              )}
            />
          </div>
        </div>

        <div className="ml-2 flex items-center gap-1.5">
          {isProcessing && <PulsatingDots className="text-blue-400" />}
          <span className={clsx(
            'text-xs font-semibold whitespace-nowrap',
            isFailed ? 'text-red-100' : 'text-gray-100',
          )}
          >
            {isNil(errorMessage) ? STATUS_LABELS[scanStatus] : errorMessage}
          </span>
          <KeyboardArrowUpIcon className="text-blue-400 !w-5 !h-5 shrink-0" />
        </div>
      </div>
    </div>
  );
}
